// @ts-check
// mobile-swipe.js — Edge-swipe gestures for the mobile sidebar.
//
// A right-edge leftward swipe opens the #panel-lists drawer (Notes/Tasks/
// Schedule, cycled via the h2 headers — same as desktop); a rightward swipe
// or overlay tap closes it.
//
// Was: references core/app-state.js's `panelLists` and `mobileMediaQuery`
// classic-script globals. Phase 4.5: those module-invisible bindings are
// replaced with local lookups via `document.getElementById('panel-lists')`
// and `window.matchMedia('(pointer: coarse)')` (same value as app-state's
// constant on non-Electron hosts).

import { coarsePointerQuery } from '../utils/coarse-pointer.js';

const mobileOverlay = document.getElementById('mobile-overlay') as any;
const _panelLists = document.getElementById('panel-lists') as any;
// app-state.js uses a no-op MediaQueryList on Electron — we don't have to
// match that here because mobile-swipe is gated on isMobileView() and
// Electron never matches pointer:coarse.
let touchStartX = 0;
let touchStartY = 0;
let touchStartTime = 0;
const SWIPE_THRESHOLD = 50;
const SWIPE_MAX_Y = 80;

/** @returns {boolean} */
function isMobileView() {
  return coarsePointerQuery.matches;
}

/** @returns {void} */
function openPanel() {
  if (!isMobileView()) return;
  _panelLists.classList.add('mobile-open-right');
  mobileOverlay.classList.add('active');
}

/** @returns {void} */
function closeMobilePanels() {
  _panelLists.classList.remove('mobile-open-right');
  mobileOverlay.classList.remove('active');
}

mobileOverlay.addEventListener('click', closeMobilePanels);

const EDGE_WIDTH = 30;

document.addEventListener(
  'touchstart',
  (e) => {
    if (!isMobileView()) return;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    touchStartTime = Date.now();
  },
  { passive: true }
);

document.addEventListener(
  'touchend',
  (e) => {
    if (!isMobileView()) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
    const elapsed = Date.now() - touchStartTime;
    const screenWidth = window.innerWidth;

    if (elapsed > 500 || dy > SWIPE_MAX_Y) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    // Reject gestures that drift more vertical than horizontal even when
    // dy is below SWIPE_MAX_Y. On narrow phones a long-but-shallow scroll
    // could otherwise nudge dx past the threshold and steal the gesture
    // from the scroll surface.
    if (dy > Math.abs(dx)) return;

    const open = _panelLists.classList.contains('mobile-open-right');

    if (dx < 0) {
      // Leftward swipe from the right edge opens the drawer.
      if (!open && touchStartX >= screenWidth - EDGE_WIDTH) {
        openPanel();
      }
    } else {
      // Rightward swipe while the drawer is open dismisses it.
      if (open) closeMobilePanels();
    }
  },
  { passive: true }
);

window.addEventListener('resize', () => {
  if (!isMobileView()) closeMobilePanels();
});
